import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from '@tanstack/react-location';
import { isFunction } from 'lodash-es';
import {
  createPaymentIntent,
  CreatePaymentIntentBody,
  getPaymentClientSecretWithAuthToken,
  SetupFutureUsage,
} from '@frontend/api-weave-pay';
import appConfig from '@frontend/env';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { DisabledHandler, FailureHandler, isStripeCardError, SuccessHandler } from '../../types';

const { PAY_BACKEND_API } = appConfig;

interface UseSubmitPaymentStripeProps {
  requestId?: string;
  requireCaptcha?: boolean;
  onDisabled?: DisabledHandler;
  onFailure?: FailureHandler;
  onSuccess?: SuccessHandler;
  origin?: string;
}

interface SubmitPaymentProps {
  paymentRequestBody?: CreatePaymentIntentBody;
  token?: string | null;
  onSubmitError?: () => void;
  onCardError?: () => void;
}

export const useSubmitPaymentStripe = ({
  onDisabled,
  onFailure,
  onSuccess,
  origin,
  requestId,
  requireCaptcha,
}: UseSubmitPaymentStripeProps) => {
  const { t } = useTranslation('weave-pay');
  const stripe = useStripe();
  const elements = useElements();
  const alerts = useAlert();
  const navigate = useNavigate();

  const handleBuildUrl = () => {
    switch (origin) {
      case 'PAYMENT_PORTAL':
        return `${PAY_BACKEND_API}/payments/v2/requests/${requestId}/payments`;
      case 'LOCATION_PORTAL':
      case 'ONLINE_SCHEDULING':
        return `${PAY_BACKEND_API}/payments/v2/portal/${requestId}/payments`;
      default:
        return '';
    }
  };

  const getPaymentIntent = async (requestBody: CreatePaymentIntentBody, token: string | null) => {
    let clientSecret;
    let paymentId;
    if (requireCaptcha && !token) {
      throw new Error('Captcha token is required for online bill pay');
    }

    const url = handleBuildUrl();

    try {
      const {
        paymentToken,
        paymentId: paymentIdOnSuccess,
        links: { self: paymentIntentUrl },
      } = await createPaymentIntent({
        url,
        requestBody,
        ...(origin !== 'PAYMENT_PORTAL' && { locationId: requestId }),
        ...(!!requireCaptcha && token && { captchaToken: token }),
      });

      const { paymentIntent } = await getPaymentClientSecretWithAuthToken({
        url: paymentIntentUrl,
        authToken: paymentToken,
        ...(origin !== 'PAYMENT_PORTAL' && { locationId: requestId }),
      });
      clientSecret = paymentIntent.clientSecret;
      paymentId = paymentIdOnSuccess;
    } catch (err) {
      console.error('Error creating payment intent', err);

      if (err instanceof HttpError) {
        if (err.status === 429) {
          onDisabled?.();
          return {};
        }
      }

      if (!isFunction(onFailure)) navigate({ to: '/error' });
      else if (err instanceof HttpError || isStripeCardError(err) || err instanceof Error) onFailure?.(err);
    }
    return { clientSecret, paymentId };
  };

  const handleSubmit = async ({ onSubmitError, onCardError, paymentRequestBody, token }: SubmitPaymentProps) => {
    if (!stripe || !elements) {
      const errMsg = t('Stripe not loaded yet. Try again!');
      alerts.error({
        autoDismissAfter: 12000,
        message: errMsg,
      });
      onFailure?.(new Error(errMsg));
      return;
    }
    if (!paymentRequestBody) {
      alerts.error({
        message: t('Payment Request not complete'),
      });
      return;
    }

    elements.update({
      setupFutureUsage: paymentRequestBody.setupFutureUsage === SetupFutureUsage.offSession ? 'off_session' : undefined,
    });

    const { clientSecret, paymentId } = await getPaymentIntent(paymentRequestBody, token || null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        alerts.warning({
          autoDismissAfter: 12000,
          message: t('Invalid information provided. Please fix the errors and resubmit.'),
        });
        onSubmitError?.();
      }
    } catch (err) {
      alerts.warning({
        autoDismissAfter: 12000,
        message: t('Invalid information provided. Please fix the errors and resubmit.'),
      });
    }
    try {
      if (clientSecret) {
        const { error: confirmPaymentError } = await stripe.confirmPayment({
          elements,
          clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}${window.location.pathname}?amount=${paymentRequestBody.amount}&firstName=${paymentRequestBody.person?.firstName}&lastName=${paymentRequestBody?.person?.lastName}&receiptEmail=${paymentRequestBody.person?.email}&paymentId=${paymentRequestBody.paymentId}`,
          },
        });
        if (!confirmPaymentError) {
          const successData = {
            amount: paymentRequestBody.amount,
            person: {
              firstName: paymentRequestBody.person?.firstName,
              lastName: paymentRequestBody.person?.lastName,
              email: paymentRequestBody.person?.email,
            },
            paymentId,
          };
          onSuccess?.(successData);
        } else if (confirmPaymentError.type === 'card_error') {
          alerts.error({
            autoDismissAfter: 12000,
            message:
              confirmPaymentError.message ||
              t('Error processing your card. Please check your information and try again.'),
          });
          onCardError?.();
        } else if (confirmPaymentError.param === 'setup_future_usage') {
          alerts.error({
            autoDismissAfter: 12000,
            message: t('There was an error processing your payment. Wait 30 seconds and try again.'),
          });
        } else if (confirmPaymentError) {
          alerts.error({
            autoDismissAfter: 12000,
            message: t('Error submitting payment. Please try again.'),
          });
        }
        if (confirmPaymentError) onFailure?.(confirmPaymentError);
      }
    } catch (err) {
      alerts.error({
        autoDismissAfter: 12000,
        message: t('Error processing payment with Stripe. Try again!'),
      });
      if (err instanceof Error) {
        onFailure?.(err);
      }
    }
  };

  return { handleSubmit };
};
